import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type DragEvent, type ChangeEvent } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import LanguageIcon from "@mui/icons-material/Language";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PageHeader } from "@/components/PageHeader";
import { useAutoTour } from "@/components/walkthrough/useAutoTour";
import { PROFILE_TOUR } from "@/components/walkthrough/tours";
import { VerifiedSkillsEditor } from "@/components/profile/VerifiedSkillsEditor";
import { PersonalInfoCard } from "@/components/profile/PersonalInfoCard";
import { ProfileCompletenessCard } from "@/components/profile/ProfileCompletenessCard";
import { UsageCard } from "@/components/UsageCard";
import { useCandidate } from "@/context/CandidateContext";
import { extractPdfText } from "@/lib/pdf";
import { parseResume } from "@/server/parse-resume";
import { ingestGithub } from "@/server/ingest-github";
import { ingestPortfolio } from "@/server/ingest-portfolio";
import { ingestLinkedInText } from "@/server/ingest-linkedin-text";
import { ingestLinkedInPdf } from "@/server/ingest-linkedin-pdf";
import { ingestLinkedInUrl } from "@/server/ingest-linkedin-url";
import { forgetSource } from "@/server/forget-source";
import { reindexCandidateChunks } from "@/server/reindex-candidate";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { candidateQueryKey } from "@/lib/queries/candidate";
import { profileQueryKey, updateProfile } from "@/lib/queries/profile";
import { supabase } from "@/integrations/supabase/client";
import { exportAccountData, deleteAccount } from "@/server/account-data";
import { toast } from "sonner";

export const Route = createFileRoute("/app/profile")({
  component: ProfilePage,
  head: () => ({ meta: [{ title: "Profile — JobYoda" }] }),
});

const MAX_PDF_BYTES = 10 * 1024 * 1024; // 10 MB
const URL_REGEX = /^https?:\/\/[^\s]+\.[^\s]+$/i;

function extractGithubUsername(url: string): string | null {
  const m = url.match(/github\.com\/([A-Za-z0-9-]+)/i);
  return m ? m[1] : null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

type SyncStep = "idle" | "resume" | "github" | "portfolio" | "linkedin" | "done";
type SourceKey = "resume" | "github" | "portfolio" | "linkedin";

function ProfilePage() {
  const theme = useTheme();
  const { profile, refetchProfile } = useCandidate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  useAutoTour(PROFILE_TOUR, "/app/profile");

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [linkedinText, setLinkedinText] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");

  const [syncing, setSyncing] = useState(false);
  const [step, setStep] = useState<SyncStep>("idle");
  const [forgetting, setForgetting] = useState<SourceKey | null>(null);

  const linkedinPdfInputRef = useRef<HTMLInputElement | null>(null);
  const [linkedinPdfUploading, setLinkedinPdfUploading] = useState(false);
  const [linkedinUrlFetching, setLinkedinUrlFetching] = useState(false);

  // Seed inputs from the persisted profile ONCE per identity per source.
  // Re-seeds when a section is removed (value clears) or first loads, but
  // never overwrites the user's in-progress typing (effect keys are the
  // persisted values, not on every render).
  useEffect(() => {
    if (profile.github.username) {
      setGithubUrl((cur) =>
        cur.length === 0 ? `https://github.com/${profile.github.username}` : cur,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.github.username]);

  useEffect(() => {
    const first = profile.portfolio.links[0];
    if (first) {
      setPortfolioUrl((cur) => (cur.length === 0 ? first : cur));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.portfolio.links[0]]);

  useEffect(() => {
    if (profile.linkedin.profileUrl) {
      setLinkedinUrl((cur) =>
        cur.length === 0 ? (profile.linkedin.profileUrl as string) : cur,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.linkedin.profileUrl]);

  useEffect(() => {
    if (profile.linkedin.rawText) {
      setLinkedinText((cur) =>
        cur.length === 0 ? (profile.linkedin.rawText as string) : cur,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.linkedin.rawText]);

  const linkedinError = linkedinUrl.length > 0 && !URL_REGEX.test(linkedinUrl);
  const githubError = githubUrl.length > 0 && !URL_REGEX.test(githubUrl);
  const portfolioError = portfolioUrl.length > 0 && !URL_REGEX.test(portfolioUrl);

  const githubUsername = githubUrl && !githubError ? extractGithubUsername(githubUrl) : null;

  const hasValidInput =
    (file && !fileError) ||
    !!githubUsername ||
    (portfolioUrl && !portfolioError) ||
    linkedinText.trim().length >= 50 ||
    (linkedinUrl.length > 0 && !linkedinError);

  const handleFiles = (files: FileList | null) => {
    setFileError(null);
    const f = files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      setFileError("Only PDF files are supported.");
      return;
    }
    if (f.size > MAX_PDF_BYTES) {
      setFileError(`File is too large (${formatBytes(f.size)}). Max 10 MB.`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const onDragLeave = () => setIsDragOver(false);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const clearFile = () => {
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getAuthHeader = async () => {
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    if (!token) throw new Error("You must be signed in.");
    return { authorization: `Bearer ${token}` };
  };

  const uploadResumeFile = async (
    f: File,
  ): Promise<{ filePath: string; fileName: string } | null> => {
    if (!user?.id) return null;
    const path = `${user.id}/resume.pdf`;
    const { error } = await supabase.storage
      .from("resumes")
      .upload(path, f, { upsert: true, contentType: "application/pdf" });
    if (error) {
      console.error("Resume upload failed:", error);
      return null;
    }
    return { filePath: path, fileName: f.name };
  };

  const handleSync = async () => {
    setSyncing(true);
    const errors: string[] = [];
    try {
      const headers = await getAuthHeader();

      // 1) Resume — upload PDF first, then parse
      if (file) {
        setStep("resume");
        try {
          const text = await extractPdfText(file);
          if (!text || text.length < 50) {
            throw new Error("Could not extract text from this PDF. Try a text-based PDF.");
          }
          const stored = await uploadResumeFile(file);
          await parseResume({
            data: {
              resumeText: text,
              filePath: stored?.filePath,
              fileName: stored?.fileName ?? file.name,
            },
            headers,
          });
          // Clear the staged file once parsed; "On file" row will show it.
          clearFile();
        } catch (e) {
          errors.push(`Resume: ${e instanceof Error ? e.message : "failed"}`);
        }
      }

      // 2) GitHub
      if (githubUsername) {
        setStep("github");
        try {
          await ingestGithub({ data: { username: githubUsername }, headers });
        } catch (e) {
          errors.push(`GitHub: ${e instanceof Error ? e.message : "failed"}`);
        }
      }

      // 3) Portfolio
      if (portfolioUrl && !portfolioError) {
        setStep("portfolio");
        try {
          await ingestPortfolio({ data: { url: portfolioUrl }, headers });
        } catch (e) {
          errors.push(`Portfolio: ${e instanceof Error ? e.message : "failed"}`);
        }
      }

      // 4) LinkedIn — prefer URL fetch (Jina Reader), fall back to pasted text.
      const hasUrl = linkedinUrl.trim().length > 0 && !linkedinError;
      const hasText = linkedinText.trim().length >= 50;
      if (hasUrl || hasText) {
        setStep("linkedin");
        try {
          if (hasUrl && !hasText) {
            await ingestLinkedInUrl({
              data: { profileUrl: linkedinUrl.trim() },
              headers,
            });
          } else if (hasText) {
            await ingestLinkedInText({
              data: {
                text: linkedinText.trim(),
                profileUrl: hasUrl ? linkedinUrl.trim() : undefined,
              },
              headers,
            });
          }
        } catch (e) {
          errors.push(`LinkedIn: ${e instanceof Error ? e.message : "failed"}`);
        }
      }

      if (user?.id) {
        await queryClient.invalidateQueries({ queryKey: candidateQueryKey(user.id) });
      }
      await refetchProfile();

      setStep("done");
      if (errors.length === 0) {
        toast.success("Profile synced from all sources.");
      } else if (errors.length < 4) {
        toast.warning(`Synced with issues: ${errors.join(" · ")}`);
      } else {
        toast.error(errors.join(" · "));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to sync profile.");
    } finally {
      setSyncing(false);
      setTimeout(() => setStep("idle"), 1200);
    }
  };

  const handleForget = async (source: SourceKey) => {
    setForgetting(source);
    try {
      const headers = await getAuthHeader();
      await forgetSource({ data: { source }, headers });
      // Clear the matching local input so the seeding effects can re-fire later.
      if (source === "github") setGithubUrl("");
      if (source === "portfolio") setPortfolioUrl("");
      if (source === "linkedin") {
        setLinkedinUrl("");
        setLinkedinText("");
      }
      if (source === "resume") clearFile();
      if (user?.id) {
        await queryClient.invalidateQueries({ queryKey: candidateQueryKey(user.id) });
      }
      await refetchProfile();
      toast.success(`Removed ${source} data.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `Failed to remove ${source}.`);
    } finally {
      setForgetting(null);
    }
  };

  const handleReset = () => {
    clearFile();
    setLinkedinUrl(profile.linkedin.profileUrl ?? "");
    setLinkedinText(profile.linkedin.rawText ?? "");
    setGithubUrl(
      profile.github.username ? `https://github.com/${profile.github.username}` : "",
    );
    setPortfolioUrl(profile.portfolio.links[0] ?? "");
  };

  const handleLinkedInPdf = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (linkedinPdfInputRef.current) linkedinPdfInputRef.current.value = "";
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("LinkedIn export must be a PDF.");
      return;
    }
    if (f.size > MAX_PDF_BYTES) {
      toast.error(`File is too large (${formatBytes(f.size)}). Max 10 MB.`);
      return;
    }
    setLinkedinPdfUploading(true);
    try {
      const text = await extractPdfText(f);
      if (!text || text.length < 50) {
        throw new Error("Could not extract text from this PDF. Try a text-based PDF.");
      }
      const headers = await getAuthHeader();
      await ingestLinkedInPdf({
        data: {
          text,
          fileName: f.name,
          profileUrl: linkedinUrl && !linkedinError ? linkedinUrl : undefined,
        },
        headers,
      });
      if (user?.id) {
        await queryClient.invalidateQueries({ queryKey: candidateQueryKey(user.id) });
      }
      await refetchProfile();
      toast.success("LinkedIn PDF parsed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to parse LinkedIn PDF.");
    } finally {
      setLinkedinPdfUploading(false);
    }
  };

  const handleLinkedInUrlFetch = async () => {
    if (!linkedinUrl || linkedinError) {
      toast.error("Enter your public LinkedIn profile URL first.");
      return;
    }
    setLinkedinUrlFetching(true);
    try {
      const headers = await getAuthHeader();
      const result = await ingestLinkedInUrl({
        data: { profileUrl: linkedinUrl.trim() },
        headers,
      });
      if (user?.id) {
        await queryClient.invalidateQueries({ queryKey: candidateQueryKey(user.id) });
      }
      await refetchProfile();
      toast.success(
        result.skillsAdded > 0
          ? `Added ${result.skillsAdded} skills from LinkedIn.`
          : "LinkedIn profile fetched.",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to fetch LinkedIn URL.");
    } finally {
      setLinkedinUrlFetching(false);
    }
  };

  const stepLabel: Record<SyncStep, string> = {
    idle: "Analyze & Sync Profile",
    resume: "Parsing resume…",
    github: "Fetching GitHub…",
    portfolio: "Reading portfolio…",
    linkedin: "Parsing LinkedIn…",
    done: "Synced",
  };

  // What's "on file" today
  const resumeOnFile = profile.resume.fileName || profile.resume.summary;
  const resumeSkillCount = profile.verifiedSkills.filter((s) => s.source === "resume").length;
  const githubOnFile = profile.github.username;
  const portfolioOnFile = profile.portfolio.links.length > 0;
  const linkedinOnFile =
    profile.linkedin.rawText ||
    profile.linkedin.headline ||
    profile.linkedin.pdfFileName;

  return (
    <Box>
      <PageHeader
        title="Your profile"
        subtitle="Upload your resume and link your professional accounts. We'll synthesize a verified candidate profile."
      />

      <TargetRoleCard />

      <Box data-tour="profile-completeness">
        <ProfileCompletenessCard />
      </Box>

      <Box>
        <UsageCard />
      </Box>

      <Box data-tour="profile-personal">
        <PersonalInfoCard />
      </Box>

      <RagIndexCard />

      <AwardsAndCertsCard />
      <ReparseResumeCard />

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Profile ingestion
          </Typography>

          {/* RESUME SECTION */}
          {resumeOnFile && (
            <OnFileRow
              icon={<PictureAsPdfIcon color="primary" />}
              label="Resume on file"
              detail={
                profile.resume.fileName
                  ? `${profile.resume.fileName} · parsed ✓ (${resumeSkillCount} skills)`
                  : `Parsed ✓ (${resumeSkillCount} skills, no original file stored)`
              }
              onForget={() => handleForget("resume")}
              forgetting={forgetting === "resume"}
              actionLabel="Replace"
            />
          )}

          {/* Dropzone */}
          <Box
            data-tour="profile-resume-drop"
            onClick={() => fileInputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            role="button"
            tabIndex={0}
            sx={{
              border: "2px dashed",
              borderColor: isDragOver ? "primary.main" : "divider",
              borderRadius: 2,
              p: { xs: 3, sm: 5 },
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
              backgroundColor: isDragOver
                ? alpha(theme.palette.primary.main, 0.06)
                : "transparent",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 44, color: "primary.main", mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {resumeOnFile ? "Replace your resume PDF" : "Drop your resume PDF here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse · PDF only · max 10 MB
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              hidden
              onChange={onFileChange}
            />
          </Box>

          {fileError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFileError(null)}>
              {fileError}
            </Alert>
          )}

          {file && !fileError && (
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={<PictureAsPdfIcon />}
                label={`${file.name} · ${formatBytes(file.size)}`}
                onDelete={clearFile}
                deleteIcon={
                  <IconButton size="small" aria-label="remove file">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                color="primary"
                variant="outlined"
                sx={{
                  maxWidth: "100%",
                  "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                }}
              />
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* URL fields */}
          <Stack spacing={2}>
            {githubOnFile && (
              <OnFileRow
                icon={<GitHubIcon color="action" />}
                label="GitHub on file"
                detail={`@${profile.github.username} · ${profile.github.totalRepos} repos · ${profile.github.stars}★`}
                onForget={() => handleForget("github")}
                forgetting={forgetting === "github"}
              />
            )}
            <TextField
              label="GitHub URL"
              placeholder="https://github.com/your-handle"
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value)}
              error={githubError}
              helperText={
                githubError
                  ? "Enter a valid URL starting with http(s)://"
                  : githubUsername
                    ? `Will fetch repos for @${githubUsername}`
                    : " "
              }
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <GitHubIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {portfolioOnFile && (
              <OnFileRow
                icon={<LanguageIcon color="action" />}
                label="Portfolio on file"
                detail={`${profile.portfolio.links.length} link${profile.portfolio.links.length === 1 ? "" : "s"} · ${profile.portfolio.cases.length} case${profile.portfolio.cases.length === 1 ? "" : "s"}`}
                onForget={() => handleForget("portfolio")}
                forgetting={forgetting === "portfolio"}
              />
            )}
            <TextField
              label="Portfolio URL"
              placeholder="https://your-portfolio.dev"
              value={portfolioUrl}
              onChange={(e) => setPortfolioUrl(e.target.value)}
              error={portfolioError}
              helperText={
                portfolioError
                  ? "Enter a valid URL starting with http(s)://"
                  : "We'll fetch the page and extract case studies + skills."
              }
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Divider />
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <LinkedInIcon color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                LinkedIn
              </Typography>
            </Stack>
            {linkedinOnFile && (
              <OnFileRow
                icon={<LinkedInIcon color="action" />}
                label="LinkedIn on file"
                detail={[
                  profile.linkedin.headline ?? null,
                  profile.linkedin.pdfFileName
                    ? `PDF: ${profile.linkedin.pdfFileName}`
                    : profile.linkedin.rawText
                      ? `${profile.linkedin.rawText.length} chars pasted`
                      : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Saved"}
                onForget={() => handleForget("linkedin")}
                forgetting={forgetting === "linkedin"}
              />
            )}
            <Alert severity="info" sx={{ py: 0.5 }}>
              Paste your LinkedIn profile URL — we'll fetch it automatically.
              No PDF needed.
            </Alert>
            <TextField
              label="LinkedIn profile URL"
              placeholder="https://www.linkedin.com/in/your-handle"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              error={linkedinError}
              helperText={
                linkedinError
                  ? "Enter a valid URL starting with http(s)://"
                  : "We'll read your public profile and extract skills + headline."
              }
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkedInIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={
                linkedinUrlFetching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleLinkedInUrlFetch}
              disabled={
                linkedinUrlFetching ||
                syncing ||
                !linkedinUrl ||
                linkedinError
              }
            >
              {linkedinUrlFetching ? "Fetching from LinkedIn…" : "Fetch profile from URL"}
            </Button>

            <Accordion
              elevation={0}
              disableGutters
              sx={{
                mt: 1,
                bgcolor: "transparent",
                "&:before": { display: "none" },
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1.5,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}
              >
                <Typography variant="body2" color="text.secondary">
                  Other ways to add LinkedIn (PDF or paste text)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        linkedinPdfUploading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <CloudUploadIcon />
                        )
                      }
                      onClick={() => linkedinPdfInputRef.current?.click()}
                      disabled={linkedinPdfUploading || syncing}
                    >
                      {linkedinPdfUploading
                        ? "Parsing LinkedIn PDF…"
                        : profile.linkedin.pdfFileName
                          ? "Replace LinkedIn PDF"
                          : "Upload LinkedIn PDF"}
                    </Button>
                    <input
                      ref={linkedinPdfInputRef}
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handleLinkedInPdf}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.75 }}
                    >
                      In LinkedIn: your profile → More → Save to PDF, then upload
                      it here. The file isn't stored — only the extracted text.
                    </Typography>
                  </Box>

                  <TextField
                    label="Paste your LinkedIn profile text"
                    placeholder="Paste your About, Experience, and Skills sections here…"
                    value={linkedinText}
                    onChange={(e) => setLinkedinText(e.target.value)}
                    fullWidth
                    multiline
                    minRows={5}
                    maxRows={14}
                    helperText={
                      linkedinText.length > 0 && linkedinText.length < 50
                        ? "Paste at least a paragraph (50+ chars)."
                        : `${linkedinText.length} chars`
                    }
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>

          {/* Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 3, justifyContent: "flex-end" }}
          >
            <Button
              variant="text"
              color="inherit"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              disabled={syncing}
            >
              Reset form
            </Button>
            <Button
              data-tour="profile-sync"
              variant="contained"
              color="primary"
              startIcon={
                syncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              onClick={handleSync}
              disabled={!hasValidInput || syncing}
            >
              {syncing ? stepLabel[step] : "Analyze & Sync Profile"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <VerifiedSkillsEditor />

      <PrivacyDataCard />
    </Box>
  );
}

function AwardsAndCertsCard() {
  const { profile } = useCandidate();
  const awards = profile.resume.awards ?? [];
  const certs = profile.resume.certifications ?? [];
  if (awards.length === 0 && certs.length === 0) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Awards & certifications
        </Typography>
        {certs.length > 0 && (
          <Box sx={{ mb: awards.length > 0 ? 2 : 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Certifications ({certs.length})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              {certs.map((c, i) => (
                <Chip key={`${c}-${i}`} label={c} size="small" />
              ))}
            </Stack>
          </Box>
        )}
        {awards.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Awards & honors ({awards.length})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              {awards.map((a, i) => (
                <Chip key={`${a}-${i}`} label={a} size="small" color="primary" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ReparseResumeCard() {
  const { profile, refetchProfile } = useCandidate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [reparsing, setReparsing] = useState(false);
  const hasResumeText = !!profile.resume.rawText && profile.resume.rawText.length >= 50;
  if (!hasResumeText) return null;

  const handleReparse = async () => {
    if (!profile.resume.rawText) return;
    setReparsing(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      const result = await parseResume({
        data: {
          resumeText: profile.resume.rawText,
          fileName: profile.resume.fileName ?? undefined,
          filePath: profile.resume.filePath ?? undefined,
        },
        headers: { authorization: `Bearer ${token}` },
      });
      if (user?.id) {
        await queryClient.invalidateQueries({ queryKey: candidateQueryKey(user.id) });
        await queryClient.invalidateQueries({ queryKey: profileQueryKey(user.id) });
      }
      await refetchProfile();
      toast.success(
        `Re-parsed: ${result.experienceCount} jobs, ${result.skillCount} skills, ${result.awardCount} awards.`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Re-parse failed.");
    } finally {
      setReparsing(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Re-parse resume with latest model
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We improved the parser to capture every work experience and award.
              Click to re-extract from your existing resume — no re-upload needed.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleReparse}
            disabled={reparsing}
            startIcon={reparsing ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          >
            {reparsing ? "Re-parsing…" : "Re-parse resume"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function OnFileRow({
  icon,
  label,
  detail,
  onForget,
  forgetting,
  actionLabel = "Remove",
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
  onForget: () => void;
  forgetting: boolean;
  actionLabel?: string;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.25,
        mb: 1,
        borderRadius: 1.5,
        backgroundColor: alpha(theme.palette.success.main, 0.06),
        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
        flexWrap: "wrap",
      }}
    >
      <CheckCircleIcon color="success" fontSize="small" />
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, wordBreak: "break-word" }}
        >
          {detail}
        </Typography>
      </Box>
      <Button
        size="small"
        color="error"
        variant="text"
        startIcon={
          forgetting ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <DeleteOutlineIcon fontSize="small" />
          )
        }
        onClick={onForget}
        disabled={forgetting}
      >
        {actionLabel}
      </Button>
    </Box>
  );
}

function RagIndexCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ chunksCreated: number; totalChunks?: number; errors: string[] } | null>(
    null,
  );

  const handleReindex = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error("Not signed in");
      const res = await reindexCandidateChunks({
        headers: { authorization: `Bearer ${token}` },
      });
      setResult(res);
      if (res.chunksCreated > 0) {
        toast.success(`Indexed ${res.chunksCreated} chunks for Q&A retrieval.`);
      } else {
        toast.error(res.errors[0] ?? "Nothing to index yet.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reindex failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Q&A retrieval index
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Build a searchable vector index of your resume, LinkedIn, portfolio, and experience.
              Powers the Q&A page with grounded, STAR-method answers.
            </Typography>
            {result && result.chunksCreated > 0 && (
              <Typography variant="caption" color="success.main" sx={{ display: "block", mt: 1 }}>
                ✓ {result.chunksCreated} chunks indexed
              </Typography>
            )}
            {result && result.errors.length > 0 && (
              <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>
                {result.errors[0]}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            onClick={handleReindex}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          >
            {loading ? "Indexing…" : "Reindex my history"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TargetRoleCard() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");
  const [initial, setInitial] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("target_role")
          .eq("id", userId)
          .maybeSingle();
        if (cancelled) return;
        const v = (data?.target_role as string | null) ?? "";
        setValue(v);
        setInitial(v);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const next = value.trim() || null;
      await updateProfile(userId, { target_role: next });
      setInitial(next ?? "");
      await queryClient.invalidateQueries({ queryKey: candidateQueryKey(userId) });
      toast.success("Target role saved. Open Career Path and click Regenerate to refresh.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save target role.");
    } finally {
      setSaving(false);
    }
  };

  const dirty = initial !== value.trim();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-end" } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Target role
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The role you're aiming for. Drives career-path branches, learning queue priorities, and success scoring.
            </Typography>
            <TextField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. Director of Design"
              fullWidth
              size="small"
              disabled={loading}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading || !dirty}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function PrivacyDataCard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      const result = await exportAccountData({
        headers: { authorization: `Bearer ${token}` },
      });
      const blob = new Blob([result.json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `jobyoda-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      await deleteAccount({ headers: { authorization: `Bearer ${token}` } });
      await supabase.auth.signOut().catch(() => {});
      toast.success("Your account and all data have been deleted.");
      setConfirmOpen(false);
      navigate({ to: "/" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card sx={{ mt: 3, borderColor: alpha(theme.palette.error.main, 0.3) }} variant="outlined">
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Privacy & data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          You own your data. Export everything we have on you, or permanently delete your account.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "center" }, mb: 3 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Export your data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Download a JSON file containing your profile, applications, jobs,
              materials, notes, interview sessions, and learning queue.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={
              exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
            }
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Preparing…" : "Download export"}
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "center" } }}
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
              <WarningAmberIcon color="error" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "error.main" }}>
                Delete account
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Permanently remove your profile, all uploaded files, and every
              application, job, and material. This cannot be undone.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              setConfirmText("");
              setConfirmOpen(true);
            }}
          >
            Delete my account
          </Button>
        </Stack>
      </CardContent>

      <Dialog
        open={confirmOpen}
        onClose={() => (deleting ? null : setConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="error" /> Delete your account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will permanently delete your profile, resume, ingested LinkedIn
            and GitHub data, all jobs, applications, materials, notes, interview
            sessions, and the learning queue. This action <strong>cannot be undone</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            Type <strong>DELETE</strong> to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting || confirmText.trim() !== "DELETE"}
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />
            }
          >
            {deleting ? "Deleting…" : "Permanently delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

